// TrackBuddyHome.jsx
import React, { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Line } from "react-chartjs-2";
import "chart.js/auto";

const TABS = [
  { id: "about", label: "About", icon: "ℹ️" },
  { id: "track", label: "Track", icon: "📝" },
  { id: "logs", label: "Logs", icon: "📜" },
  { id: "dashboard", label: "Dashboard", icon: "📊" },
  { id: "ai", label: "AI Buddy", icon: "🤖" },
];

const INITIAL_TRACKERS = [
  {
    id: "quality_score",
    name: "Product Quality Score",
    emoji: "🏭",
    description: "Rate your product quality on a scale of 1–10.",
    unitLabel: "/10",
    persona: "MSME Owner",
  },
  {
    id: "daily_expense",
    name: "Daily Household Expense",
    emoji: "🏠",
    description: "Track how much you spend in a day.",
    unitLabel: "₹",
    persona: "Housewife",
  },
  {
    id: "study_hours",
    name: "Study Hours",
    emoji: "📚",
    description: "Record how many hours you studied today.",
    unitLabel: "hrs",
    persona: "Student",
  },
  {
    id: "yield_kg",
    name: "Crop Yield",
    emoji: "🌾",
    description: "Track harvest quantity per day/plot.",
    unitLabel: "kg",
    persona: "Farmer",
  },
];

const INITIAL_ENTRIES = [
  {
    id: 1,
    trackerId: "quality_score",
    value: 8.5,
    userLabel: "Owner",
    note: "New batch after process change",
    createdAt: new Date().toISOString(),
  },
  {
    id: 2,
    trackerId: "quality_score",
    value: 7.8,
    userLabel: "Supervisor A",
    note: "Night shift",
    createdAt: new Date(Date.now() - 3600 * 1000 * 5).toISOString(),
  },
  {
    id: 3,
    trackerId: "daily_expense",
    value: 950,
    userLabel: "Home",
    note: "Groceries + milk",
    createdAt: new Date(Date.now() - 3600 * 1000 * 10).toISOString(),
  },
  {
    id: 4,
    trackerId: "study_hours",
    value: 2.5,
    userLabel: "Child",
    note: "Math + Science",
    createdAt: new Date(Date.now() - 3600 * 1000 * 20).toISOString(),
  },
  {
    id: 5,
    trackerId: "yield_kg",
    value: 120,
    userLabel: "Plot A",
    note: "Morning harvest",
    createdAt: new Date(Date.now() - 3600 * 1000 * 30).toISOString(),
  },
];

// Small helper to format time nicely
const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const MiniBarChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <p className="text-xs text-slate-400">
        No data yet. Add a few readings and your mini chart will appear here.
      </p>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.value)) || 1;

  return (
    <div className="mt-2 flex items-end gap-1 rounded-2xl border border-slate-800 bg-slate-950 px-2 py-3">
      {data.map((d) => {
        const height = Math.max(10, (d.value / maxValue) * 70); // 10–80px
        return (
          <div
            key={d.id}
            className="flex flex-1 flex-col items-center justify-end gap-1"
          >
            <div
              className="w-full rounded-md bg-gradient-to-t from-blue-700 to-blue-400"
              style={{ height: `${height}px` }}
              title={`${d.value}`}
            />
            <span className="truncate text-[0.55rem] text-slate-400">
              {d.userLabel || "—"}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const TrackBuddyHome = () => {
  const navigate = useNavigate();
  const [showCharts, setShowCharts] = useState(false);
  const [activeTab, setActiveTab] = useState("about");
  const [trackers] = useState(INITIAL_TRACKERS);
  const [entries, setEntries] = useState(INITIAL_ENTRIES);
  const [selectedTrackerId, setSelectedTrackerId] = useState(
    INITIAL_TRACKERS[0]?.id
  );
  const [logsFilterTrackerId, setLogsFilterTrackerId] = useState("all");
  const [newEntry, setNewEntry] = useState({
    value: "",
    userLabel: "",
    note: "",
  });

  const selectedTracker = useMemo(
    () => trackers.find((t) => t.id === selectedTrackerId),
    [trackers, selectedTrackerId]
  );

   const [insight, setInsight] = useState(null);

  // --------------------------------------------------------
  // DUMMY DATA (This will later come from your backend API)
  // --------------------------------------------------------
  const sampleLogs = [
    { id: 1, date: "2025-12-01", shift: "Day", quality: 8.5 },
    { id: 2, date: "2025-12-01", shift: "Night", quality: 7.8 },
    { id: 3, date: "2025-12-02", shift: "Day", quality: 8.2 },
    { id: 4, date: "2025-12-02", shift: "Night", quality: 7.5 },
    { id: 5, date: "2025-12-03", shift: "Day", quality: 8.3 },
    { id: 6, date: "2025-12-03", shift: "Night", quality: 7.6 },
  ];

  const filteredLogs = useMemo(() => {
    if (logsFilterTrackerId === "all") return entries.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return entries
      .filter((e) => e.trackerId === logsFilterTrackerId)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [entries, logsFilterTrackerId]);

  const dashboardStats = useMemo(() => {
    const byTracker = {};
    entries.forEach((e) => {
      if (!byTracker[e.trackerId]) {
        byTracker[e.trackerId] = {
          tracker: trackers.find((t) => t.id === e.trackerId),
          values: [],
        };
      }
      byTracker[e.trackerId].values.push(e.value);
    });

    return Object.values(byTracker).map(({ tracker, values }) => {
      const total = values.reduce((sum, v) => sum + v, 0);
      const avg = total / values.length;
      const min = Math.min(...values);
      const max = Math.max(...values);

      // Very simple "consistency" score: lower spread → higher score
      const spread = max - min || 1;
      const roughScore = Math.max(
        40,
        Math.min(100, 100 - (spread / (avg || 1)) * 30)
      );

      return {
        trackerId: tracker.id,
        trackerName: tracker.name,
        emoji: tracker.emoji,
        count: values.length,
        avg: avg.toFixed(2),
        min,
        max,
        consistencyScore: Math.round(roughScore),
      };
    });
  }, [entries, trackers]);

  const handleNewEntryChange = (field, value) => {
    setNewEntry((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddEntry = (e) => {
    e.preventDefault();
    if (!selectedTrackerId) return;

    const num = parseFloat(newEntry.value);
    if (Number.isNaN(num)) {
      alert("Please enter a valid numeric value.");
      return;
    }

    const newItem = {
      id: Date.now(),
      trackerId: selectedTrackerId,
      value: num,
      userLabel: newEntry.userLabel || "Me",
      note: newEntry.note,
      createdAt: new Date().toISOString(),
    };

    setEntries((prev) => [newItem, ...prev]);
    setNewEntry({ value: "", userLabel: "", note: "" });
  };

  const handleAskAIBuddy = () => {
      navigate("/agenticsim");
  };

  const getTrendChartData = (sampleLogs) => {
    return {
      labels: sampleLogs.map((x) => x.date),
      datasets: [
        {
          label: "Quality Score",
          data: sampleLogs.map((x) => x.quality),
          borderColor: "#3b82f6",
          backgroundColor: "rgba(59,130,246,0.3)",
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 4,
          pointBackgroundColor: "#60a5fa",
        },
      ],
    };
  };

  const getTrendChartOptions = () => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: { color: "#60a5fa" },
        },
      },
      scales: {
        x: {
          ticks: { color: "#60a5fa" },
          grid: { color: "rgba(30,58,138,0.2)" },
        },
        y: {
          ticks: { color: "#60a5fa" },
          grid: { color: "rgba(30,58,138,0.2)" },
        },
      },
    };
  };

  const handleGenerateSampleInsight = () => {
    setShowCharts(true);
  };

  const getShiftChartData = (sampleLogs) => {
    const day = sampleLogs.filter((x) => x.shift === "Day");
    const night = sampleLogs.filter((x) => x.shift === "Night");

    const avg = (arr) =>
      arr.reduce((s, x) => s + x.quality, 0) / arr.length;

    return {
      labels: ["Day", "Night"],
      datasets: [
        {
          label: "Average Quality",
          data: [avg(day), avg(night)],
          backgroundColor: ["#3b82f6", "#1e40af"],
        },
      ],
    };
  };



  // --- TAB RENDERERS ---

  const renderAboutTab = () => (
    <div className="space-y-4">
      <p className="text-sm md:text-base text-slate-200">
        <span className="font-semibold text-blue-400">TrackBuddy</span> is your
        everyday consistency companion. It helps you{" "}
        <span className="font-semibold">track, log, and see patterns</span> in
        anything that matters — from quality in your business to expenses at
        home, study hours, farm yields, or team performance.
      </p>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-blue-500/70 bg-slate-900/70 p-4 shadow-lg">
          <h3 className="text-sm font-semibold text-blue-300">
            🧱 Simple Measurement
          </h3>
          <p className="mt-2 text-xs text-slate-200">
            Add trackers like “Daily Expense”, “Product Quality”, or “Study
            Hours”. Log a few values every day — TrackBuddy handles the rest.
          </p>
        </div>
        <div className="rounded-2xl border border-sky-500/70 bg-slate-900/70 p-4 shadow-lg">
          <h3 className="text-sm font-semibold text-sky-300">
            📊 Logs &amp; Dashboard
          </h3>
          <p className="mt-2 text-xs text-slate-200">
            See your latest readings, mini charts, and simple scores so you can
            quickly understand what is stable and what needs attention.
          </p>
        </div>
        <div className="rounded-2xl border border-indigo-500/70 bg-slate-900/70 p-4 shadow-lg">
          <h3 className="text-sm font-semibold text-indigo-300">
            🤖 AI-Ready (Optional)
          </h3>
          <p className="mt-2 text-xs text-slate-200">
            AI hooks help you spot patterns and anomalies in plain language.
            You control when and how AI is used — or you can keep it fully
            manual.
          </p>
        </div>
      </div>

      <div className="mt-2 rounded-2xl bg-gradient-to-r from-blue-700/70 via-indigo-700/70 to-sky-600/70 p-[1px]">
        <div className="rounded-2xl bg-slate-950/95 p-4">
          <h3 className="text-sm font-semibold text-blue-100">
            Vision: Gauge R&amp;R for Everyone
          </h3>
          <p className="mt-2 text-xs md:text-sm text-slate-200">
            Below the surface, TrackBuddy is built on Gauge R&amp;R thinking:
            repeatability and reproducibility of your measurements. On the
            surface, you see only simple trackers, logs, and charts. This lets a
            housewife, MSME owner, worker, student, or farmer use structured
            measurement without ever touching complex statistics.
          </p>
        </div>
      </div>
    </div>
  );

  const renderTrackTab = () => (
    <div className="space-y-4">
      {/* Tracker selection */}
      <div>
        <p className="text-xs text-slate-300 mb-2">
          1. Choose a tracker · 2. Enter today’s reading · 3. Save &amp; see it
          in logs and dashboard.
        </p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {trackers.map((t) => {
            const isActive = t.id === selectedTrackerId;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedTrackerId(t.id)}
                className={`flex min-w-[140px] flex-col items-start rounded-2xl border px-3 py-2 text-left text-xs transition ${
                  isActive
                    ? "border-blue-500 bg-blue-900/60 text-blue-50 shadow-lg"
                    : "border-slate-700 bg-slate-900/80 text-slate-200 hover:border-blue-500/70"
                }`}
              >
                <span className="text-lg">{t.emoji}</span>
                <span className="mt-1 font-semibold">{t.name}</span>
                <span className="mt-0.5 text-[0.65rem] text-slate-300">
                  {t.persona}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tracker info + form */}
      {selectedTracker && (
        <div className="grid gap-4 md:grid-cols-[1.4fr,1.2fr]">
          <div className="rounded-2xl border border-slate-800 bg-slate-950/90 p-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-100">
              <span className="text-xl">{selectedTracker.emoji}</span>
              <span>{selectedTracker.name}</span>
            </h3>
            <p className="mt-1 text-xs text-slate-300">
              {selectedTracker.description}
            </p>
            <form
              onSubmit={handleAddEntry}
              className="mt-4 space-y-3 text-xs text-slate-100"
            >
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="mb-1 block text-[0.7rem] text-slate-300">
                    Value ({selectedTracker.unitLabel})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newEntry.value}
                    onChange={(e) =>
                      handleNewEntryChange("value", e.target.value)
                    }
                    className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100 outline-none focus:border-blue-500"
                    placeholder={`e.g. 8.5, 950, 2.5`}
                    required
                  />
                </div>
                <div className="w-32">
                  <label className="mb-1 block text-[0.7rem] text-slate-300">
                    Who
                  </label>
                  <input
                    type="text"
                    value={newEntry.userLabel}
                    onChange={(e) =>
                      handleNewEntryChange("userLabel", e.target.value)
                    }
                    className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100 outline-none focus:border-blue-500"
                    placeholder="Me / Shift A"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[0.7rem] text-slate-300">
                  Note (optional)
                </label>
                <textarea
                  rows={2}
                  value={newEntry.note}
                  onChange={(e) =>
                    handleNewEntryChange("note", e.target.value)
                  }
                  style={{height: "200px"}}
                  className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100 outline-none focus:border-blue-500"
                  placeholder="Any quick context – new batch, special event, etc."
                />
              </div>
              <button
                type="submit"
                className="mt-1 w-full rounded-xl bg-blue-600 py-2 text-xs font-semibold text-slate-50 shadow-lg hover:bg-blue-500 active:scale-[0.98]"
              >
                ➕ Save Reading
              </button>
            </form>
          </div>

          {/* Side panel: last few entries + mini chart */}
          <div className="rounded-2xl border border-slate-800 bg-slate-950/90 p-4">
            <h3 className="text-sm font-semibold text-slate-100">
              Recent Readings
            </h3>
            <p className="mt-1 text-[0.7rem] text-slate-400">
              Last few values for this tracker only.
            </p>
            <MiniBarChart
              data={entries
                .filter((e) => e.trackerId === selectedTrackerId)
                .slice(0, 7)
                .reverse()}
            />
            <ul className="mt-3 space-y-1.5 max-h-32 overflow-y-auto text-[0.7rem] text-slate-200">
              {entries
                .filter((e) => e.trackerId === selectedTrackerId)
                .slice(0, 5)
                .map((e) => (
                  <li
                    key={e.id}
                    className="flex justify-between gap-2 rounded-xl border border-slate-800 bg-slate-900/80 px-2 py-1.5"
                  >
                    <div>
                      <div className="font-semibold">
                        {e.value} {selectedTracker.unitLabel}
                      </div>
                      <div className="text-[0.65rem] text-slate-400">
                        {e.userLabel} · {formatDateTime(e.createdAt)}
                      </div>
                      {e.note && (
                        <div className="mt-0.5 text-[0.65rem] text-slate-300">
                          {e.note}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              {entries.filter((e) => e.trackerId === selectedTrackerId).length ===
                0 && (
                <li className="text-[0.7rem] text-slate-400">
                  No readings yet. Add the first one using the form.
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );

  const renderLogsTab = () => (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        {/* <p className="text-xs text-slate-300">
          This is your central log. You can later load this from backend for
          history, exports, etc.
        </p> */}
        <select
          value={logsFilterTrackerId}
          onChange={(e) => setLogsFilterTrackerId(e.target.value)}
          className="w-full h-10 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-100 outline-none focus:border-blue-500 md:w-52"
        >
          <option value="all">All trackers</option>
          {trackers.map((t) => (
            <option key={t.id} value={t.id}>
              {t.emoji} {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-950">
        <table className="min-w-full border-collapse text-[0.7rem] text-slate-100">
          <thead className="bg-slate-900/90">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Tracker</th>
              <th className="px-3 py-2 text-left font-semibold">Value</th>
              <th className="px-3 py-2 text-left font-semibold">Who</th>
              <th className="px-3 py-2 text-left font-semibold">When</th>
              <th className="px-3 py-2 text-left font-semibold">Note</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((e) => {
              const tracker = trackers.find((t) => t.id === e.trackerId);
              return (
                <tr
                  key={e.id}
                  className="border-t border-slate-800 hover:bg-slate-900/70"
                >
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-col">
                      <span className="font-semibold">
                        {tracker?.emoji} {tracker?.name}
                      </span>
                      <span className="text-[0.6rem] text-slate-400">
                        {tracker?.persona}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    {e.value} {tracker?.unitLabel}
                  </td>
                  <td className="px-3 py-2 align-top">{e.userLabel}</td>
                  <td className="px-3 py-2 align-top text-slate-300">
                    {formatDateTime(e.createdAt)}
                  </td>
                  <td className="px-3 py-2 align-top text-slate-200">
                    {e.note || "—"}
                  </td>
                </tr>
              );
            })}
            {filteredLogs.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-4 text-center text-slate-400"
                >
                  No logs yet. Add some readings in the{" "}
                  <span className="text-blue-400 font-semibold">Track</span> tab.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDashboardTab = () => (
    <div className="space-y-4">
      <p className="text-xs text-slate-300">
        A quick dashboard for your trackers. Later you can expand this into a
        full analytics screen or export.
      </p>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {dashboardStats.map((stat) => (
          <div
            key={stat.trackerId}
            className="rounded-2xl border border-slate-800 bg-slate-950 p-4"
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                  <span className="text-lg">{stat.emoji}</span>
                  <span className="line-clamp-2">{stat.trackerName}</span>
                </div>
                <div className="mt-1 text-[0.65rem] text-slate-400">
                  {stat.count} readings logged
                </div>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[0.65rem] text-slate-400">
                  Consistency
                </span>
                <span className="text-sm font-semibold text-blue-300">
                  {stat.consistencyScore}%
                </span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[0.65rem]">
              <div className="rounded-xl bg-slate-900/80 px-2 py-1.5">
                <div className="text-slate-400">Avg</div>
                <div className="text-slate-100">{stat.avg}</div>
              </div>
              <div className="rounded-xl bg-slate-900/80 px-2 py-1.5">
                <div className="text-slate-400">Min</div>
                <div className="text-slate-100">{stat.min}</div>
              </div>
              <div className="rounded-xl bg-slate-900/80 px-2 py-1.5">
                <div className="text-slate-400">Max</div>
                <div className="text-slate-100">{stat.max}</div>
              </div>
            </div>
          </div>
        ))}
        {dashboardStats.length === 0 && (
          <p className="text-xs text-slate-400">
            No data yet. Once you start logging readings, summary cards will
            appear here.
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-blue-700/70 bg-slate-950/90 p-4">
        <h3 className="text-sm font-semibold text-blue-200">
          How this connects to Gauge R&amp;R
        </h3>
        <p className="mt-1 text-[0.7rem] text-slate-200">
          As you capture repeated readings for the same tracker and multiple
          people log values, TrackBuddy can later compute more formal
          repeatability and reproducibility metrics. For now, it gives you an
          approximate consistency score based on spread and stability, so users
          don&apos;t have to understand any statistics to benefit.
        </p>
      </div>
    </div>
  );

  const renderAITab = () => (
    <div className="space-y-4">
      <p className="text-sm text-slate-200">
        TrackBuddy is AI-ready, but not AI-dependent. These are just{" "}
        <span className="font-semibold text-blue-300">hooks</span>. You can plug
        in your own backend, LLM agents, or orchestration.
      </p>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-blue-600/70 bg-slate-900/80 p-4 shadow-lg">
          <h3 className="text-sm font-semibold text-blue-200">
            🔍 Pattern Assistant
          </h3>
          <p className="mt-2 text-xs text-slate-200">
            Summarise patterns such as &quot;evening shifts are more stable&quot;
            or &quot;second week of the month shows higher spending&quot;.
          </p>
          <span className="mt-3 inline-flex rounded-full bg-blue-900/80 px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-wide text-blue-200">
            Design Stage
          </span>
        </div>

        <div className="rounded-2xl border border-blue-600/70 bg-slate-900/80 p-4 shadow-lg">
          <h3 className="text-sm font-semibold text-blue-200">
            🚨 Anomaly Watch
          </h3>
          <p className="mt-2 text-xs text-slate-200">
            Flag days or readings that are &quot;unusual&quot; compared to your
            usual pattern and suggest what to check.
          </p>
          <span className="mt-3 inline-flex rounded-full bg-blue-900/80 px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-wide text-blue-200">
            Prototype Ready
          </span>
        </div>

        <div className="rounded-2xl border border-blue-600/70 bg-slate-900/80 p-4 shadow-lg">
          <h3 className="text-sm font-semibold text-blue-200">
            🧭 Buddy Coach
          </h3>
          <p className="mt-2 text-xs text-slate-200">
            Give tiny, friendly suggestions: &quot;Log this for 3 more days&quot;,
            &quot;Try separating morning and evening readings&quot;, etc.
          </p>
          <span className="mt-3 inline-flex rounded-full bg-blue-900/80 px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-wide text-blue-200">
            Coming Soon
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleAskAIBuddy}
          className="rounded-full bg-blue-600 px-4 py-2 text-xs font-semibold text-slate-50 shadow-lg hover:bg-blue-500 active:scale-[0.98]"
        >
          💬 Open AI Buddy
        </button>
        <button
          onClick={handleGenerateSampleInsight}
          className="rounded-full border border-blue-500/70 bg-slate-950 px-4 py-2 text-xs font-semibold text-blue-200 hover:bg-slate-900"
        >
          📈 Generate Sample Insight
        </button>
      </div>
      {showCharts && (
        <>
          <div className="mt-10">
            <h2 className="mb-2 text-lg font-semibold text-blue-400">
              Quality Trend
            </h2>

            <div className="h-64 w-full rounded-xl border border-blue-800/40 bg-slate-950 p-3">
              <Line
                data={getTrendChartData(sampleLogs)}
                options={getTrendChartOptions()}
              />
            </div>
          </div>

          <div className="mt-10">
            <h2 className="mb-2 text-lg font-semibold text-blue-400">
              Day vs Night Comparison
            </h2>

            <div className="h-64 w-full rounded-xl border border-blue-800/40 bg-slate-950 p-3">
              <Line
                data={getShiftChartData(sampleLogs)}
                options={getTrendChartOptions()}
              />
            </div>
          </div>
        </>
      )}
      {/* <p className="text-[0.7rem] text-slate-400">
        These buttons currently fire simple alerts. In your implementation, you
        can pass the tracker list and recent logs to your backend and return
        structured insights or chat responses.
      </p> */}
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case "about":
        return renderAboutTab();
      case "track":
        return renderTrackTab();
      case "logs":
        return renderLogsTab();
      case "dashboard":
        return renderDashboardTab();
      case "ai":
        return renderAITab();
      default:
        return renderAboutTab();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black text-slate-50">
      <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 pb-10 pt-6 md:pt-8">
        {/* Header */}
        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/70 bg-blue-900/20 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-wide text-blue-300">
            <span>TrackBuddy</span>
            <span className="h-1 w-1 rounded-full bg-blue-300" />
            <span>Gauge R&amp;R</span>
          </div>
          <h1 className="text-xl lg:text-2xl font-semibold text-yellow-200">
            🔷 TrackBuddy
          </h1>
          {/* <p className="max-w-2xl text-xs md:text-sm text-slate-300">
            A dark-theme, mobile-first home for your trackers, logs, dashboards,
            and AI hooks. All data here is dummy, stored in local state — ready
            for you to connect to your backend, database, and agents.
          </p> */}
        </header>

        {/* Tabs */}
        <section className="rounded-3xl border border-slate-800 bg-slate-950/80 p-3 shadow-xl">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Navigate TrackBuddy
            </h2>
            <span className="rounded-full bg-slate-900 px-2 py-1 text-[0.6rem] text-slate-400">
              Transform your repeated work
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 md:grid-cols-5">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex flex-col items-center justify-center rounded-2xl border px-2 py-3 text-center text-[0.7rem] font-medium transition ${
                    isActive
                      ? "border-blue-500 bg-blue-900/70 text-blue-50 shadow-lg"
                      : "border-slate-800 bg-slate-900/80 text-slate-300 hover:border-blue-500/70 hover:bg-slate-900"
                  }`}
                >
                  <span className="mb-[2px] text-base">{tab.icon}</span>
                  <span className="leading-tight">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Content */}
        <main className="rounded-3xl border border-slate-800 bg-slate-950/90 p-4 shadow-2xl md:p-6">
          {renderActiveTab()}
        </main>

        {/* Footer */}
        <footer className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[0.65rem] text-slate-500">
          <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/about-us" className="hover:text-blue-300">About</Link>
              <Link to="/privacy-policy" className="hover:text-blue-300">Privacy Policy</Link>
              <Link to="/terms-of-use" className="hover:text-blue-300">Terms of Use</Link>
              <Link to="/contact-us" className="hover:text-blue-300">Contact</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default TrackBuddyHome;
